import type { ClientUser } from "../../shared/types/bsso";
export interface User extends ClientUser {}

/**
 * Clears the redirecting flag from the URL if it exists
 */
const clearRedirectingFlag = (): void => {
  if (window.location.search.includes("redirecting=true")) {
    const newUrl = new URL(window.location.href);
    newUrl.searchParams.delete("redirecting");

    // Use replaceState to avoid adding to browser history
    window.history.replaceState({}, "", newUrl.toString());
    console.log("Cleared redirecting flag from URL");
  }
};

/**
 * Safely redirects to login with error handling and fallback
 */
const safeRedirectToLogin = (): void => {
  import("../utility/redirect-to-login")
    .then(({ redirectToLoginWithReturnPath }) => {
      redirectToLoginWithReturnPath();
    })
    .catch((error) => {
      console.error("Failed to load redirect utility, using fallback:", error);

      // Fallback: manually construct the redirect URL
      const currentPath =
        window.location.pathname +
        window.location.search +
        window.location.hash;
      const state = encodeURIComponent(
        JSON.stringify({ redirectUrl: currentPath }),
      );

      // Hard navigation fallback
      window.location.href = `/bsso/login?state=${state}`;
    });
};
export const getUser = (notif?: {
  addError: (opts: { message: string }) => void;
}): Promise<User | null> =>
  fetch("/bsso/privilege").then(
    async (res) => {
      if (res.status === 401 || res.status === 403) {
        console.log("Authentication failed, status:", res.status);

        // Only show notification and redirect if not already in the process of redirecting
        if (notif && !window.location.search.includes("redirecting")) {
          notif.addError({
            message: "Session is stale. Redirecting to login again.",
          });

          // Add a flag to prevent multiple redirects
          const newUrl = new URL(window.location.href);
          newUrl.searchParams.set("redirecting", "true");
          window.history.replaceState({}, "", newUrl.toString());

          setTimeout(() => {
            safeRedirectToLogin();
          }, 3000);
        }
        return null;
      }
      if (res.status === 204) {
        // Clear redirecting flag if it exists, since we got a response (even if no user)
        clearRedirectingFlag();
        return null;
      }
      if (res.ok) {
        // Clear redirecting flag on successful authentication
        clearRedirectingFlag();
        return await res.json();
      }
      if (res.status !== 403) {
        console.warn("Failed to authenticate:", await res.text());
      }
      return null;
    },
    (err) => {
      if (
        notif &&
        err &&
        err.name === "TypeError" &&
        err.message === "Failed to fetch"
      ) {
        notif.addError({
          message:
            "Network error. Refreshing. Please check your connection or try again later.",
        });
        setTimeout(() => {
          safeRedirectToLogin();
        }, 3000);
      }
      console.error("Failed to authenticate:", err);
      return null;
    },
  );

export const getUserUuid = (notif?: {
  addError: (opts: { message: string }) => void;
}): Promise<any> =>
  fetch("/bsso/uuid")
    .then((res) => {
      if (res.status === 401 || res.status === 403) {
        if (notif)
          notif.addError({
            message: "Session is stale. Redirecting to login again.",
          });
        setTimeout(() => {
          safeRedirectToLogin();
        }, 3000);
        return;
      }
      return res.json();
    })
    .catch((error) => {
      if (
        notif &&
        error &&
        error.name === "TypeError" &&
        error.message === "Failed to fetch"
      ) {
        notif.addError({
          message:
            "Network error. Refreshing. Please check your connection or try again later.",
        });
        setTimeout(() => {
          safeRedirectToLogin();
        }, 3000);
      }
      setTimeout(() => {
        window.location.href = "/bsso/login";
      }, 3000);
      throw error;
    });
